import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@services/auth';
import { showcaseProjects, type ShowcaseProject } from '@data/showcaseProjects';
import { stylePresets } from '@data/stylePresets';
import { trackActivityEvent, usePageActivityTracker } from '@services/activityTracker';
import { useCountUp } from '@utils/useCountUp';

const steps = [
	{
		number: '01',
		icon: 'bi-journal-plus',
		title: 'Create your garden plan',
		description:
			'Short questionnaire about your space and style. Add a photo anytime for more tailored suggestions.'
	},
	{
		number: '02',
		icon: 'bi-envelope-paper-heart',
		title: 'Receive offers from gardeners',
		description:
			'Compare budgets and timelines in one place when gardeners and companies respond to your plan.'
	},
	{
		number: '03',
		icon: 'bi-kanban',
		title: 'Turn a plan into a project',
		description:
			'Choose an offer you like and track progress—ideas, photos, and messages stay in one hub.'
	}
];

/** Style mood images from the questionnaire presets (distinct from showcase before/after). */
function getHowItWorksStepsWithPhotos() {
	const keys = ['modern', 'mediterranean', 'traditional'] as const;
	return steps.map((step, i) => {
		const preset = stylePresets.find((s) => s.key === keys[i]);
		return {
			...step,
			image: preset?.imageUrl,
			imageAlt: preset ? `${preset.title} style inspiration (questionnaire mood)` : ''
		};
	});
}

type QuizStyleKey = 'romantic' | 'sculptural' | 'earthy' | 'zen';

const quizStyles: Record<QuizStyleKey, { emoji: string; title: string; description: string }> = {
	romantic: {
		emoji: '🌸',
		title: 'Romantic Cottage Garden',
		description: 'Soft flowers, winding paths, and cozy corners full of charm.'
	},
	sculptural: {
		emoji: '🟪',
		title: 'Modern Sculptural Garden',
		description: 'Clean geometry, statement planting, and a sharp contemporary feel.'
	},
	earthy: {
		emoji: '🌞',
		title: 'Mediterranean Earthy Garden',
		description: 'Sun-loving textures, warm tones, and low-maintenance planting.'
	},
	zen: {
		emoji: '🧘',
		title: 'Japanese Zen Garden',
		description: 'Calm structure, balanced greenery, and a peaceful retreat energy.'
	}
};

type QuizOption = {
	label: string;
	emoji: string;
	scores: Partial<Record<QuizStyleKey, number>>;
};

const quizQuestions: { prompt: string; icon: string; options: QuizOption[] }[] = [
	{
		prompt: "What's your garden vibe?",
		icon: '✨',
		options: [
			{ label: 'Wild & romantic', emoji: '🌸', scores: { romantic: 2, earthy: 1 } },
			{ label: 'Clean & sculptural', emoji: '🟪', scores: { sculptural: 2, zen: 1 } },
			{ label: 'Sun-drenched & earthy', emoji: '🌞', scores: { earthy: 2, romantic: 1 } },
			{ label: 'Peaceful & zen', emoji: '🧘', scores: { zen: 2, sculptural: 1 } }
		]
	},
	{
		prompt: 'How much time can you dedicate weekly?',
		icon: '⏰',
		options: [
			{ label: 'Almost none - keep it easy', emoji: '😌', scores: { earthy: 2, sculptural: 1 } },
			{ label: 'A couple of hours', emoji: '🌱', scores: { zen: 1, romantic: 1, earthy: 1, sculptural: 1 } },
			{ label: 'I love getting my hands dirty!', emoji: '🌻', scores: { romantic: 2, zen: 1 } },
			{ label: 'It depends on the season', emoji: '🍂', scores: { earthy: 1, romantic: 1, zen: 1 } }
		]
	},
	{
		prompt: 'What matters most to you?',
		icon: '🎯',
		options: [
			{ label: 'Beautiful flowers & colours', emoji: '🌹', scores: { romantic: 2 } },
			{ label: 'A space to relax & unwind', emoji: '🛖', scores: { zen: 2 } },
			{ label: 'Growing food & herbs', emoji: '🥬', scores: { earthy: 2 } },
			{ label: 'Wildlife & eco impact', emoji: '🦋', scores: { zen: 1, earthy: 1, romantic: 1 } }
		]
	}
];

export default function SeekerLanding() {
	const { user } = useAuth();
	const location = useLocation();
	usePageActivityTracker('seeker_home');
	const logoSrc = `${import.meta.env.BASE_URL}logo-transparent.png`;
	const heroImageSrc = `${import.meta.env.BASE_URL}hero-seeker.png`;

	const inviteCodeFromUrl = (() => {
		try {
			const params = new URLSearchParams(location.search);
			const raw = params.get('invite') || params.get('code');
			return raw && raw.trim() ? raw.trim() : null;
		} catch {
			return null;
		}
	})();

	const gardenerInviteCodeFromUrl = (() => {
		try {
			const params = new URLSearchParams(location.search);
			const raw = params.get('gardenerInvite');
			return raw && raw.trim() ? raw.trim() : null;
		} catch {
			return null;
		}
	})();

	const loginHref = gardenerInviteCodeFromUrl
		? `/login?gardenerInvite=${encodeURIComponent(gardenerInviteCodeFromUrl)}`
		: inviteCodeFromUrl
			? `/login?invite=${encodeURIComponent(inviteCodeFromUrl)}`
			: '/login';
	const seekerLoginHref = loginHref.includes('?') ? `${loginHref}&entry=seeker` : `${loginHref}?entry=seeker`;

	const seekerPlansHref = '/app/seeker/plans';
	const tryQuestionnaireHref = '/try-questionnaire?entry=seeker';
	const [quizStep, setQuizStep] = useState(0);
	const [quizAnswers, setQuizAnswers] = useState<QuizOption[]>([]);
	const [sliderValues, setSliderValues] = useState<Record<string, number>>({});
	const [transformIndex, setTransformIndex] = useState(0);
	const [selectedProject, setSelectedProject] = useState<ShowcaseProject | null>(null);
	const [hasTriedBeforeAfterSlider, setHasTriedBeforeAfterSlider] = useState(false);

	useEffect(() => {
		try {
			sessionStorage.setItem('publicEntry', 'seeker');
		} catch {
			// Ignore storage access issues (private mode, blocked storage).
		}
	}, []);

	const primaryHref = user
		? user.role === 'seeker'
			? seekerPlansHref
			: '/app'
		: tryQuestionnaireHref;
	const primaryLabel = user
		? user.role === 'seeker'
			? 'Go to my plans'
			: 'Open app'
		: 'Try the questionnaire — free';

	const secondaryHref = user
		? user.role === 'seeker'
			? '/app/seeker/create-plan'
			: loginHref
		: loginHref;
	const secondaryLabel = user ? (user.role === 'seeker' ? 'Create a plan (signed in)' : 'Log in') : 'Log in';
	const transformProjects = useMemo(() => [...showcaseProjects], []);
	const feedbackProjects = useMemo(() => showcaseProjects.slice(0, 3), []);
	const currentTransform = transformProjects[transformIndex] ?? transformProjects[0];

	useEffect(() => {
		const initial: Record<string, number> = {};
		showcaseProjects.forEach((p) => {
			initial[p.id] = 50;
		});
		setSliderValues((prev) => ({ ...initial, ...prev }));
	}, []);

	useEffect(() => {
		if (!selectedProject) return;
		document.body.style.overflow = 'hidden';
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setSelectedProject(null);
		};
		document.addEventListener('keydown', onKey);
		return () => {
			document.body.style.overflow = '';
			document.removeEventListener('keydown', onKey);
		};
	}, [selectedProject]);

	const quizResult = useMemo(() => {
		if (quizAnswers.length !== quizQuestions.length) return null;
		const scores: Record<QuizStyleKey, number> = {
			romantic: 0,
			sculptural: 0,
			earthy: 0,
			zen: 0
		};
		quizAnswers.forEach((answer) => {
			(Object.keys(scores) as QuizStyleKey[]).forEach((key) => {
				scores[key] += answer.scores[key] || 0;
			});
		});
		const winner = (Object.entries(scores) as [QuizStyleKey, number][]).sort((a, b) => b[1] - a[1])[0][0];
		return quizStyles[winner];
	}, [quizAnswers]);

	const markBeforeAfterSliderUsed = () => {
		setHasTriedBeforeAfterSlider(true);
	};

	const handleQuizAnswer = (option: QuizOption) => {
		if (quizStep >= quizQuestions.length) return;
		void trackActivityEvent({
			event_name: 'seeker_home_quiz_answer',
			properties: { quiz_step: quizStep + 1, answer_label: option.label }
		});
		const nextAnswers = [...quizAnswers, option];
		setQuizAnswers(nextAnswers);
		setQuizStep((s) => Math.min(s + 1, quizQuestions.length));
	};

	const goTransformPrev = () => {
		void trackActivityEvent({ event_name: 'seeker_home_carousel_nav', properties: { direction: 'prev' } });
		setTransformIndex((i) => (i - 1 + transformProjects.length) % transformProjects.length);
	};
	const goTransformNext = () => {
		void trackActivityEvent({ event_name: 'seeker_home_carousel_nav', properties: { direction: 'next' } });
		setTransformIndex((i) => (i + 1) % transformProjects.length);
	};

	const modalCtaHref = user?.role === 'seeker' ? seekerPlansHref : tryQuestionnaireHref;
	const modalCtaLabel = user?.role === 'seeker' ? 'Go to my plans' : 'Start questionnaire';
	const transformSliderPct = currentTransform ? sliderValues[currentTransform.id] ?? 50 : 50;
	const howStepsWithPhotos = useMemo(() => getHowItWorksStepsWithPhotos(), []);
	const gardensDesigned = Math.round(useCountUp(130));
	const gardenStyles = Math.round(useCountUp(10));
	const minutesToPlan = Math.max(1, Math.round(useCountUp(3)));

	return (
		<>
			<section className="seeker-landing-hero text-white">
				<div
					className="seeker-landing-hero-media"
					style={{ backgroundImage: `url(${heroImageSrc})` }}
					aria-hidden
				/>
				<div className="seeker-landing-hero-gradient" aria-hidden />
				<div className="container position-relative py-5 seeker-landing-hero-content">
					<div className="row justify-content-center">
						<div className="col-12 col-lg-10 col-xl-9 text-center text-lg-start">
							<div className="d-inline-flex align-items-center gap-2 seeker-landing-badge mb-4">
								<img src={logoSrc} alt="" className="seeker-landing-badge-logo" width={28} height={28} />
								<span className="seeker-landing-badge-muted">Free during beta</span>
							</div>
							<h1 className="display-4 fw-bold mb-3 seeker-landing-title">
								Turn your outdoor space into a garden you love.
							</h1>
							<p className="lead seeker-landing-lead mb-4 mb-lg-5 mx-auto mx-lg-0" style={{ maxWidth: '36rem' }}>
								Create a plan in minutes, see your ideas in one place, and connect with gardeners when you are ready—no
								account needed to try the questionnaire.
							</p>
							<div className="d-flex flex-column flex-sm-row flex-wrap gap-3 justify-content-center justify-content-lg-start">
								<Link
									to={primaryHref}
									className="btn btn-lg btn-light text-success-emphasis px-4 py-3 rounded-pill fw-semibold shadow seeker-landing-cta-primary"
									onClick={() => void trackActivityEvent({ event_name: 'seeker_home_cta_click', properties: { cta_name: 'hero_primary' } })}
								>
									<i className="bi bi-ui-checks-grid me-2" aria-hidden />
									{primaryLabel}
								</Link>
								<Link
									to={secondaryHref === loginHref ? seekerLoginHref : secondaryHref}
									className="btn btn-lg btn-outline-light px-4 py-3 rounded-pill seeker-landing-cta-secondary"
									onClick={() => void trackActivityEvent({ event_name: 'seeker_home_cta_click', properties: { cta_name: 'hero_secondary' } })}
								>
									<i className="bi bi-box-arrow-in-right me-2" aria-hidden />
									{secondaryLabel}
								</Link>
							</div>
							<p className="small seeker-landing-hero-note mt-4 mb-0">Get your first plan started in minutes.</p>
							<div className="seeker-landing-stats mt-4">
								<div className="seeker-landing-stat">
									<div className="seeker-landing-stat-value">{gardensDesigned}+</div>
									<div className="seeker-landing-stat-label">Gardens designed</div>
								</div>
								<div className="seeker-landing-stat">
									<div className="seeker-landing-stat-value">{gardenStyles}</div>
									<div className="seeker-landing-stat-label">Garden styles</div>
								</div>
								<div className="seeker-landing-stat">
									<div className="seeker-landing-stat-value">&lt;{minutesToPlan}</div>
									<div className="seeker-landing-stat-label">mins to get your plan</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			<section id="how" className="landing-section py-5">
				<div className="container">
					<div className="text-center mb-5">
						<h2 className="display-6 text-success-emphasis mb-3">How it works</h2>
						<p className="text-muted lead fs-6 mx-auto" style={{ maxWidth: '40rem' }}>
							Three steps from “I have a patch of grass” to a clear plan you can share—or turn into a real project.
						</p>
					</div>
					<div className="row g-4 align-items-stretch">
						{howStepsWithPhotos.map((step) => (
							<div key={step.number} className="col-12 col-md-4">
								<div className="seeker-step-card h-100 rounded-4 border bg-white shadow-sm overflow-hidden d-flex flex-column">
									{step.image ? (
										<div className="seeker-step-photo">
											<img src={step.image} alt={step.imageAlt} className="seeker-step-photo-img" loading="lazy" />
										</div>
									) : null}
									<div className="seeker-step-content p-3 p-md-4 flex-grow-1 d-flex flex-column">
										<div className="seeker-step-topline mb-2 mb-md-3">
											<div className="seeker-step-number text-success-emphasis mb-0">{step.number}</div>
											<div className="seeker-step-icon mb-0">
												<i className={`bi ${step.icon}`} aria-hidden />
											</div>
										</div>
										<h3 className="h5 fw-semibold text-success-emphasis mb-2 seeker-step-title">{step.title}</h3>
										<p className="text-muted small mb-0">{step.description}</p>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			<section className="landing-section py-5 bg-white">
				<div className="container">
					<div className="text-center mb-4">
						<div className="text-uppercase small fw-semibold text-success-emphasis opacity-75 mb-2">Transformations</div>
						<h2 className="h2 text-success-emphasis mb-2">See what’s possible</h2>
						<p className="text-muted mb-0">Drag the slider to compare before and after. Use the arrows to explore each story.</p>
					</div>
					{currentTransform && (
						<div className="seeker-transform-carousel mx-auto">
							<div className="seeker-transform-card rounded-4 overflow-hidden">
								<div className="seeker-before-after-wrap seeker-before-after-wrap--hero">
									<img
										src={currentTransform.before}
										alt={`${currentTransform.title} before`}
										className="w-100 seeker-transform-image seeker-transform-image--hero"
									/>
									<div className="seeker-after-layer" style={{ clipPath: `inset(0 0 0 ${transformSliderPct}%)` }}>
										<img
											src={currentTransform.after}
											alt={`${currentTransform.title} after`}
											className="w-100 seeker-transform-image seeker-transform-image--hero"
										/>
									</div>
									<div className="seeker-slider-line" style={{ left: `${transformSliderPct}%` }}>
										<span className={`seeker-slider-handle ${!hasTriedBeforeAfterSlider ? 'seeker-slider-handle--hint' : ''}`}>
											<i className="bi bi-arrow-left-right" />
										</span>
									</div>
									<span className="seeker-image-label before">Before</span>
									<span className="seeker-image-label after">After</span>
									<input
										type="range"
										min={0}
										max={100}
										value={transformSliderPct}
										onPointerDown={() => {
											if (!hasTriedBeforeAfterSlider) markBeforeAfterSliderUsed();
										}}
										onChange={(e) => {
											if (!hasTriedBeforeAfterSlider) markBeforeAfterSliderUsed();
											setSliderValues((prev) => ({
												...prev,
												[currentTransform.id]: Number(e.target.value)
											}));
										}}
										className="seeker-slider-input"
										aria-label={`Before and after slider for ${currentTransform.title}`}
									/>
								</div>
								<div className="p-3 p-md-4 bg-white border border-top-0">
									<div className="d-flex flex-column flex-md-row align-items-start align-items-md-end justify-content-between gap-3">
										<div className="flex-grow-1 pe-md-3">
											<div className="badge rounded-pill bg-success-subtle text-success-emphasis border border-success-subtle mb-2">
												{currentTransform.style}
											</div>
											<h3 className="h4 text-success-emphasis mb-1">{currentTransform.title}</h3>
											<p className="text-muted small mb-0">{currentTransform.description}</p>
										</div>
										{transformProjects.length > 1 && (
											<div className="d-flex align-items-center gap-2 align-self-end ms-md-auto">
												<button
													type="button"
													className="btn btn-outline-success rounded-circle seeker-carousel-arrow"
													onClick={goTransformPrev}
													aria-label="Previous transformation"
												>
													<i className="bi bi-chevron-left" aria-hidden />
												</button>
												<button
													type="button"
													className="btn btn-outline-success rounded-circle seeker-carousel-arrow"
													onClick={goTransformNext}
													aria-label="Next transformation"
												>
													<i className="bi bi-chevron-right" aria-hidden />
												</button>
											</div>
										)}
									</div>
									{transformProjects.length > 1 && (
										<div className="d-flex justify-content-center gap-2 mt-3 pt-2 border-top border-light-subtle">
											{transformProjects.map((p, i) => (
												<button
													key={p.id}
													type="button"
													className={`seeker-carousel-dot ${i === transformIndex ? 'is-active' : ''}`}
													onClick={() => setTransformIndex(i)}
													aria-label={`Show ${p.title}`}
													aria-current={i === transformIndex ? 'true' : undefined}
												/>
											))}
										</div>
									)}
								</div>
							</div>
						</div>
					)}
				</div>
			</section>

			<section className="landing-section py-5">
				<div className="container">
					<div className="text-center mb-4">
						<div className="text-uppercase small fw-semibold text-success-emphasis opacity-75 mb-2">Discover your style</div>
						<h2 className="h2 text-success-emphasis mb-2">What kind of garden are you?</h2>
						<p className="text-muted mb-4">3 quick questions. Instant personalised result.</p>
					</div>
					<div className="seeker-quiz-shell mx-auto">
						<div className="seeker-quiz-progress mb-4">
							{quizQuestions.map((_, idx) => (
								<div key={idx} className={`seeker-quiz-bar ${quizStep > idx ? 'is-done' : ''}`} />
							))}
						</div>
						{quizResult ? (
							<div className="seeker-quiz-result text-center">
								<div className="fs-2 mb-2">{quizResult.emoji}</div>
								<div className="text-uppercase small fw-semibold text-success-emphasis opacity-75 mb-2">Your style is</div>
								<h3 className="h2 text-success-emphasis mb-2">{quizResult.title}</h3>
								<p className="text-muted mb-4">{quizResult.description}</p>
								<div className="d-flex flex-column flex-sm-row justify-content-center gap-2">
									<Link to={tryQuestionnaireHref} className="btn btn-success rounded-pill px-4">
										Design my garden
									</Link>
									<button
										type="button"
										className="btn btn-outline-success rounded-pill px-4"
										onClick={() => {
										void trackActivityEvent({ event_name: 'seeker_home_quiz_retake' });
											setQuizAnswers([]);
											setQuizStep(0);
										}}
									>
										Retake quiz
									</button>
								</div>
							</div>
						) : (
							<>
								<div className="text-center mb-4">
									<div className="fs-4 mb-2" aria-hidden>{quizQuestions[quizStep].icon}</div>
									<h3 className="h3 text-success-emphasis">{quizQuestions[quizStep].prompt}</h3>
									<div className="small text-muted">Question {quizStep + 1} of {quizQuestions.length}</div>
								</div>
								<div className="row g-3">
									{quizQuestions[quizStep].options.map((option) => (
										<div key={option.label} className="col-12 col-md-6">
											<button
												type="button"
												className="seeker-vibe-card rounded-4 p-3 w-100 text-center border-0"
												onClick={() => handleQuizAnswer(option)}
											>
												<div className="fs-4 mb-2" aria-hidden>{option.emoji}</div>
												<div className="small fw-semibold text-success-emphasis">{option.label}</div>
											</button>
										</div>
									))}
								</div>
							</>
						)}
					</div>
				</div>
			</section>

			<section className="landing-section py-5">
				<div className="container">
					<div className="text-center mb-4">
						<div className="text-uppercase small fw-semibold text-success-emphasis opacity-75 mb-2">Loved by garden seekers</div>
						<h2 className="h2 text-success-emphasis mb-2">Real feedback, real progress</h2>
					</div>
					<div className="row g-3">
						{feedbackProjects.map((project) => (
							<div key={project.id} className="col-12 col-md-6 col-lg-4">
								<div
									role="button"
									tabIndex={0}
									className="seeker-testimonial-card rounded-4 overflow-hidden h-100 seeker-testimonial-card--clickable"
									onClick={() => {
										void trackActivityEvent({ event_name: 'seeker_home_project_open', properties: { project_id: project.id } });
										setSelectedProject(project);
									}}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											setSelectedProject(project);
										}
									}}
								>
									<img src={project.after} alt={project.title} className="w-100 seeker-feedback-image" />
									<div className="p-3">
										<div className="d-flex align-items-center justify-content-between mb-2">
											<span className="badge rounded-pill text-bg-success">{project.style}</span>
											<span className="text-warning small">★★★★★</span>
										</div>
										<p className="small text-muted mb-2">"{project.details.testimonial}"</p>
										<div className="small fw-semibold text-success-emphasis">{project.client}</div>
										<p className="text-success small mt-2 mb-0">
											<i className="bi bi-arrow-right me-1" aria-hidden />
											View project details
										</p>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{selectedProject && (
				<>
					<div
						className="modal fade show"
						style={{ display: 'block', zIndex: 30000 }}
						tabIndex={-1}
						onClick={(e) => {
							if (e.target === e.currentTarget) setSelectedProject(null);
						}}
					>
						<div className="modal-dialog modal-xl modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
							<div className="modal-content" onClick={(e) => e.stopPropagation()}>
								<div className="modal-header">
									<h2 className="modal-title fw-bold text-success-emphasis">{selectedProject.title}</h2>
									<button
										type="button"
										className="btn-close"
										onClick={() => setSelectedProject(null)}
										aria-label="Close"
									/>
								</div>
								<div className="modal-body">
									<div className="mb-4">
										<p className="lead text-muted mb-4">{selectedProject.description}</p>
										<div className="row g-4 mb-4">
											<div className="col-12 col-lg-6">
												<div
													className="position-relative"
													style={{ aspectRatio: '16/9', overflow: 'hidden', borderRadius: '1rem' }}
												>
													<span className="badge bg-dark position-absolute top-0 start-0 m-3" style={{ zIndex: 1 }}>
														Before
													</span>
													<img
														src={selectedProject.before}
														alt={`${selectedProject.title} before`}
														className="w-100 h-100"
														style={{ objectFit: 'cover', borderRadius: '1rem' }}
													/>
												</div>
											</div>
											<div className="col-12 col-lg-6">
												<div
													className="position-relative"
													style={{ aspectRatio: '16/9', overflow: 'hidden', borderRadius: '1rem' }}
												>
													<span className="badge bg-success position-absolute top-0 start-0 m-3" style={{ zIndex: 1 }}>
														After
													</span>
													<img
														src={selectedProject.after}
														alt={`${selectedProject.title} after`}
														className="w-100 h-100"
														style={{ objectFit: 'cover', borderRadius: '1rem' }}
													/>
												</div>
											</div>
										</div>
									</div>

									<div className="row g-4">
										<div className="col-12 col-lg-8">
											<div className="card border-0 shadow-sm mb-4">
												<div className="card-body p-4">
													<h3 className="h5 fw-bold text-success-emphasis mb-4">Project details</h3>
													<div className="row g-3 mb-4">
														<div className="col-6 col-md-3">
															<div className="text-muted small">Location</div>
															<div className="fw-semibold">{selectedProject.location}</div>
														</div>
														<div className="col-6 col-md-3">
															<div className="text-muted small">Area</div>
															<div className="fw-semibold">{selectedProject.details.area}</div>
														</div>
														<div className="col-6 col-md-3">
															<div className="text-muted small">Duration</div>
															<div className="fw-semibold">{selectedProject.details.duration}</div>
														</div>
														<div className="col-6 col-md-3">
															<div className="text-muted small">Budget</div>
															<div className="fw-semibold">{selectedProject.details.budget}</div>
														</div>
													</div>
													<div className="mb-4">
														<h4 className="h6 fw-semibold mb-3">Key features</h4>
														<ul className="list-unstyled">
															{selectedProject.details.features.map((feature, idx) => (
																<li key={idx} className="mb-2 d-flex align-items-start gap-2">
																	<i className="bi bi-check-circle-fill text-success mt-1" />
																	<span>{feature}</span>
																</li>
															))}
														</ul>
													</div>
													<div className="mb-4">
														<h4 className="h6 fw-semibold mb-3">Plant selection</h4>
														<div className="row g-2">
															{selectedProject.details.plants.map((plant, idx) => (
																<div key={idx} className="col-12 col-md-6">
																	<div className="d-flex align-items-center gap-2">
																		<i className="bi bi-flower1 text-success" />
																		<span className="small">{plant}</span>
																	</div>
																</div>
															))}
														</div>
													</div>
												</div>
											</div>

											<div className="card border-0 shadow-sm">
												<div className="card-body p-4">
													<h3 className="h5 fw-bold text-success-emphasis mb-4">Project story</h3>
													<div className="mb-4">
														<h4 className="h6 fw-semibold mb-3 text-warning">Challenges</h4>
														<ul className="list-unstyled">
															{selectedProject.details.challenges.map((challenge, idx) => (
																<li key={idx} className="mb-2 d-flex align-items-start gap-2">
																	<i className="bi bi-exclamation-triangle-fill text-warning mt-1" />
																	<span>{challenge}</span>
																</li>
															))}
														</ul>
													</div>
													<div>
														<h4 className="h6 fw-semibold mb-3 text-success">Solutions</h4>
														<ul className="list-unstyled">
															{selectedProject.details.solutions.map((solution, idx) => (
																<li key={idx} className="mb-2 d-flex align-items-start gap-2">
																	<i className="bi bi-lightbulb-fill text-success mt-1" />
																	<span>{solution}</span>
																</li>
															))}
														</ul>
													</div>
												</div>
											</div>
										</div>

										<div className="col-12 col-lg-4">
											<div className="card border-0 shadow-sm bg-success-subtle">
												<div className="card-body p-4">
													<h3 className="h5 fw-bold text-success-emphasis mb-3">Client testimonial</h3>
													<blockquote className="mb-0">
														<p className="text-muted mb-3">"{selectedProject.details.testimonial}"</p>
														<footer className="small text-muted">— {selectedProject.client}</footer>
													</blockquote>
												</div>
											</div>
											<div className="card border-0 shadow-sm mt-4">
												<div className="card-body p-4">
													<h3 className="h5 fw-bold text-success-emphasis mb-3">Project info</h3>
													<div className="small">
														<div className="mb-2">
															<span className="text-muted">Client:</span>
															<div className="fw-semibold">{selectedProject.client}</div>
														</div>
														<div className="mb-2">
															<span className="text-muted">Completed:</span>
															<div className="fw-semibold">
																{new Date(selectedProject.completedDate).toLocaleDateString('en-US', {
																	year: 'numeric',
																	month: 'long',
																	day: 'numeric'
																})}
															</div>
														</div>
														<div className="mb-2">
															<span className="text-muted">Style:</span>
															<div className="fw-semibold">{selectedProject.style}</div>
														</div>
													</div>
												</div>
											</div>
											<Link
												to={modalCtaHref}
												className="btn btn-success w-100 mt-4"
												onClick={() => setSelectedProject(null)}
											>
												{modalCtaLabel}
											</Link>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div
						className="modal-backdrop fade show"
						onClick={() => setSelectedProject(null)}
						style={{ opacity: 0.25, zIndex: 29900 }}
					/>
				</>
			)}

			<section className="landing-section py-5">
				<div className="container">
					<div className="seeker-bottom-cta rounded-4 p-4 p-md-5 text-center text-md-start">
						<div className="row align-items-center g-4">
							<div className="col-md-8">
								<h2 className="h4 text-success-emphasis mb-2">Ready to try it?</h2>
								<p className="text-muted mb-0 small">
									Open the full questionnaire without logging in, or sign in to go straight to your seeker dashboard.
								</p>
							</div>
							<div className="col-md-4 d-flex flex-column flex-sm-row flex-md-column gap-2 justify-content-md-end">
								<Link
									to={tryQuestionnaireHref}
									className="btn btn-success btn-lg rounded-pill"
									onClick={() => void trackActivityEvent({ event_name: 'seeker_home_cta_click', properties: { cta_name: 'footer_questionnaire' } })}
								>
									Start questionnaire
								</Link>
								<Link
									to={seekerLoginHref}
									className="btn btn-outline-success btn-lg rounded-pill"
									onClick={() => void trackActivityEvent({ event_name: 'seeker_home_cta_click', properties: { cta_name: 'footer_login' } })}
								>
									Log in
								</Link>
							</div>
						</div>
					</div>
				</div>
			</section>
		</>
	);
}
